import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, ArrowUpRight, Package, Layers, GraduationCap } from "lucide-react";
import { PRODUCTS, SOLUTIONS_TRAINING, SOLUTIONS_TALENT } from "@/lib/data";
import { ALL_COURSES } from "@/lib/learningTracks";

const SOLUTIONS = [...SOLUTIONS_TRAINING, ...SOLUTIONS_TALENT];

const SearchModal = ({ open, onClose }) => {
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (open) {
            setQuery("");
            const t = setTimeout(() => inputRef.current?.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = "";
        };
    }, [open, onClose]);

    const results = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return { products: [], solutions: [], courses: [] };

        const products = PRODUCTS.filter(
            (p) => p.title.toLowerCase().includes(q) || p.tagline?.toLowerCase().includes(q)
        ).slice(0, 5);

        const solutions = SOLUTIONS.filter(
            (s) => s.title.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q)
        ).slice(0, 5);

        const courses = ALL_COURSES.filter(
            (c) => c.title.toLowerCase().includes(q) || c.category.toLowerCase().includes(q)
        ).slice(0, 6);

        return { products, solutions, courses };
    }, [query]);

    const hasResults = results.products.length || results.solutions.length || results.courses.length;

    const goTo = (path) => {
        navigate(path);
        onClose();
    };

    if (!open) return null;

    return (
        <div
            data-testid="search-modal-overlay"
            className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center px-4 pt-24 md:pt-32"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl rounded-2xl border border-white/10 bg-[#0d0621] shadow-2xl overflow-hidden animate-fade-up"
                onClick={(e) => e.stopPropagation()}
                data-testid="search-modal-panel"
            >
                <div className="flex items-center gap-3 px-5 py-4 border-b border-white/[0.08]">
                    <Search className="w-5 h-5 text-[#75668C] shrink-0" />
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && query.trim()) {
                                goTo(`/learning-tracks?q=${encodeURIComponent(query.trim())}`);
                            }
                        }}
                        placeholder="Search products, solutions, courses..."
                        data-testid="search-modal-input"
                        className="flex-1 bg-transparent text-white placeholder-[#75668C] font-display text-[15px] focus:outline-none"
                    />
                    <button
                        onClick={onClose}
                        aria-label="Close search"
                        data-testid="search-modal-close"
                        className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[#B0A3C4] hover:text-white hover:bg-white/[0.06] transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto p-3">
                    {!query.trim() && (
                        <p className="px-3 py-6 text-center text-[#75668C] text-sm font-display">
                            Start typing to search across products, solutions & courses.
                        </p>
                    )}

                    {query.trim() && !hasResults && (
                        <p className="px-3 py-6 text-center text-[#75668C] text-sm font-display">
                            No results for &ldquo;{query}&rdquo;.
                        </p>
                    )}

                    {results.products.length > 0 && (
                        <div className="mb-2">
                            <div className="px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#75668C] font-display flex items-center gap-2">
                                <Package className="w-3.5 h-3.5" /> Products
                            </div>
                            {results.products.map((p) => (
                                <button
                                    key={p.slug}
                                    onClick={() => goTo(`/products/${p.slug}`)}
                                    data-testid={`search-result-product-${p.slug}`}
                                    className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.05] transition-colors group"
                                >
                                    <div>
                                        <div className="text-white font-display text-sm font-medium">{p.title}</div>
                                        <div className="text-[#B0A3C4] text-[12.5px]">{p.tagline}</div>
                                    </div>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-[#75668C] group-hover:text-[#E9CCFF] shrink-0" />
                                </button>
                            ))}
                        </div>
                    )}

                    {results.solutions.length > 0 && (
                        <div className="mb-2">
                            <div className="px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#75668C] font-display flex items-center gap-2">
                                <Layers className="w-3.5 h-3.5" /> Solutions
                            </div>
                            {results.solutions.map((s) => (
                                <button
                                    key={s.slug}
                                    onClick={() => goTo(`/solutions/${s.slug}`)}
                                    data-testid={`search-result-solution-${s.slug}`}
                                    className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.05] transition-colors group"
                                >
                                    <div>
                                        <div className="text-white font-display text-sm font-medium">{s.title}</div>
                                        <div className="text-[#B0A3C4] text-[12.5px] line-clamp-1">{s.description?.slice(0, 80)}</div>
                                    </div>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-[#75668C] group-hover:text-[#E9CCFF] shrink-0" />
                                </button>
                            ))}
                        </div>
                    )}

                    {results.courses.length > 0 && (
                        <div className="mb-2">
                            <div className="px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-[#75668C] font-display flex items-center gap-2">
                                <GraduationCap className="w-3.5 h-3.5" /> Courses
                            </div>
                            {results.courses.map((c) => (
                                <button
                                    key={c.id}
                                    onClick={() => goTo(`/learning-tracks?q=${encodeURIComponent(c.title)}`)}
                                    data-testid={`search-result-course-${c.id}`}
                                    className="w-full text-left flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.05] transition-colors group"
                                >
                                    <div>
                                        <div className="text-white font-display text-sm font-medium">{c.title}</div>
                                        <div className="text-[#B0A3C4] text-[12.5px]">{c.category}</div>
                                    </div>
                                    <ArrowUpRight className="w-3.5 h-3.5 text-[#75668C] group-hover:text-[#E9CCFF] shrink-0" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SearchModal;
